<template>
  <div>
    <div class="relative z-10 flex flex-col gap-6 py-6 md:mb-96">
      <div v-for="(tagGroup, tagGroupName) in props.tagGroups" :key="tagGroupName">
        <div class="mb-2">
          <Checkbox
            :id="`all-select-${tagGroupName}`"
            class="flex cursor-pointer items-center"
            :color="!isSP && type === 'Themes' && showTagImg ? 'light-fg-inverse' : 'gold'"
            :data-testid="`all-select-${tagGroupName}`"
            :model-value="isTagGroupActive(tagGroup)"
            @change="(e: Event) => onChangeTagGroup(e, tagGroupName, tagGroup)"
          >
            <span class="ml-1 text-base font-medium tracking-normal" :class="[{ 'text-light-fg-inverse': showTagImg }, { 'text-stone-700': isSP || type === 'Destinations' }]">{{ tagGroupName }}</span>
          </Checkbox>
        </div>
        <div class="flex cursor-pointer flex-wrap gap-2">
          <div v-for="tag in tagGroup" :key="tag.webPath">
            <Tag
              class="cursor-pointer"
              hover-color="bg-beige2"
              padding-x="px-4"
              service="Wabunka"
              :active="isTagActive(tag.webPath)"
              :class="[
                {
                  'md:opacity-50': !isSP && type !== 'Destinations' && showTagImg && selectedTagNameWithGroupName !== getTagNameWithGroupName(tag.name, tag.tagGroupName),
                },
              ]"
              :data-testid="`single-select-${tag.tagGroupName.concat('-', tag.name)}`"
              @click="() => onClickTag(tag.webPath, tag.name)"
              @mouseleave="() => onMouseLeaveTag()"
              @mouseover="() => onMouseOverTag(getTagNameWithGroupName(tag.name, tag.tagGroupName), tag.imgPath)"
            >
              {{ extractOtherPlaces(tag.name) }}
            </Tag>
          </div>
        </div>
      </div>
    </div>
    <!-- pointer-events-noneはブラウザにより継承の挙動が異なる可能性があるため、親要素、子要素の双方に追加 -->
    <div class="pointer-events-none invisible md:visible">
      <!-- TODO: カテゴリ検索の場合はCSSの指定が異なってくると思うので、variants or props等で切り替えができるように修正をお願いいたします -->
      <OptimizedImage
        v-if="props.defaultImgPath"
        alt="tag image"
        :class="['pointer-events-none absolute bottom-20 right-0 z-0']"
        :image-url="props.defaultImgPath"
        :optimized-image-options="{ width: TAG_IMG_WIDTH }"
      />
      <!-- MEMO: タグ画像のプリロード（currentTagImgPathが動的に切り替わるため、初回のimg読み込みが遅延して画像の切り替えにラグが生じするため、プリロードしている） -->
      <!-- プリロードと実表示で同一の最適化URLになるよう同一オプションを通す -->
      <OptimizedImage
        v-for="imgPath in allTagImgPaths"
        :key="imgPath"
        alt="preload tag image"
        :class="['pointer-events-none hidden']"
        :image-url="imgPath"
        :optimized-image-options="{ width: TAG_IMG_WIDTH }"
      />
      <template v-if="currentTagImgPath">
        <OptimizedImage
          alt="tag image"
          loading="lazy"
          :class="[
            'z-5 pointer-events-none absolute transition duration-500 ease-in-out',
            type === 'Destinations' && 'bottom-20 right-0',
            type === 'Themes' && 'bottom-0 left-0 right-0 top-0 h-full w-full object-cover object-center brightness-75',
            showTagImg ? 'opacity-100' : 'opacity-0',
          ]"
          :image-url="currentTagImgPath"
          :optimized-image-options="{ width: TAG_IMG_WIDTH }"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Tag from '@/components/atoms/Tag/Tag.vue';
import Checkbox from '@/components/molecules/Checkbox/Checkbox.vue';
import { useIsSP } from '@/composables/ui/useIsSP';
import type { WabunkaTagDictByGroupName, WabunkaTagListViewModel, WabunkaTagViewModel } from '@/models/wabunka/wabunkaTag';
import { OptimizedImage } from '@/wabunka-src/common/components';

interface Props {
  type: 'Themes' | 'Destinations' | 'Preferences';
  tagGroups: WabunkaTagDictByGroupName;
  defaultSelect: string[];
  defaultImgPath?: string;
  /**
   * selectionChangeとの違いは主にタググループを選択したときの挙動
   * selectionChange: タググループを選択した場合、それに含まれる全てのタグを配列で返す
   * onClickTagRaw: タググループを選択した場合、そのタググループ名を返す
   * @param tagName タグ名 グループタグ含む
   */
  onClickTagRaw?: (tagName: string) => void;

  toggleThemeTagImgVisibility?: (value: boolean) => void;
}
const props = defineProps<Props>();
const emits = defineEmits<{ (e: 'selectionChange', tags: Map<string, string>): void }>();

const isSP = useIsSP();

// CMS のタグ画像(imgPath)を OptimizedImage 経由で Cloudflare 画像変換(format=auto)に通して帯域削減＆AVIF/WebP 自動配信する。
// default/preload/current すべて同一の width を使い、プリロードが実表示と一致するようにする。
const TAG_IMG_WIDTH = 720;

const selectedTags = ref<Map<string, string>>(new Map(props.defaultSelect.map((tag: string) => [tag, tag])));

const allTagImgPaths = computed<string[]>(() => {
  return Object.values(props.tagGroups as WabunkaTagDictByGroupName).flatMap((tagGroup: WabunkaTagListViewModel) => tagGroup.map((tag: WabunkaTagViewModel) => tag.imgPath));
});

const onChangeTagGroup = (e: Event, tagGroupName: string, tagGroup: WabunkaTagViewModel[] | undefined) => {
  if (!tagGroup) {
    return;
  }
  props.onClickTagRaw?.(tagGroupName);
  for (const tag of tagGroup) {
    if ((e.target as HTMLInputElement).checked) {
      selectedTags.value.set(tag.webPath, tag.webPath);
    } else {
      selectedTags.value.delete(tag.webPath);
    }
  }
  emits('selectionChange', selectedTags.value);
};

const onClickTag = (tagWebPath: string, tagName: string) => {
  props.onClickTagRaw?.(tagName);
  if (selectedTags.value.has(tagWebPath)) {
    selectedTags.value.delete(tagWebPath);
  } else {
    selectedTags.value.set(tagWebPath, tagWebPath);
  }
  emits('selectionChange', selectedTags.value);
};

const isTagGroupActive = (tagGroup: WabunkaTagViewModel[] | undefined) => {
  if (!tagGroup) {
    return false;
  }
  return tagGroup.every((tag) => selectedTags.value.has(tag.webPath));
};

const isTagActive = (tag: string) => {
  return selectedTags.value.has(tag);
};

// Hoverされているタグ
const selectedTagNameWithGroupName = ref<string>('');
const currentTagImgPath = ref<string>('');
const showTagImg = ref<boolean>(false);

const onMouseOverTag = async (tagNameWithGroupName: string, tagImagePath: string) => {
  // Tag Hover時に同一Webpathを持つ別のTagもハイライトされてしまうため、Uniqueな名前を設定する
  selectedTagNameWithGroupName.value = tagNameWithGroupName;

  // タグ画像が存在しない場合は、ハイライト等を行わない
  if (tagImagePath === '') {
    return;
  }

  currentTagImgPath.value = tagImagePath;
  showTagImg.value = true;

  if (props.type === 'Themes' && props.toggleThemeTagImgVisibility) {
    props.toggleThemeTagImgVisibility(true);
  }
};

const onMouseLeaveTag = async () => {
  showTagImg.value = false;
  selectedTagNameWithGroupName.value = '';

  if (props.type === 'Themes' && props.toggleThemeTagImgVisibility) {
    props.toggleThemeTagImgVisibility(false);
  }
};

const extractOtherPlaces = (tagName: string) => {
  // タグ名に「Other places」が含まれている場合、「Other places」を返す
  return tagName.includes('Other places') ? 'Other places' : tagName;
};

const getTagNameWithGroupName = (tagName: string, tagGroupName: string) => {
  return tagGroupName.concat('-', tagName);
};
</script>
